import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart3, 
  Bot,
  Link,
  Settings,
  TrendingUp,
  Activity,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { userDetailService, UserBot, UserBotAccount } from '@/services/admin/user-detail-service';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { ReversePositionBadge } from '@/components/common/ReversePositionBadge';

interface UserBotsPerformanceTabProps {
  userId: string;
}

export const UserBotsPerformanceTab: React.FC<UserBotsPerformanceTabProps> = ({ userId }) => {
  const [bots, setBots] = useState<UserBot[]>([]);
  const [botAccounts, setBotAccounts] = useState<UserBotAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [botsData, botAccountsData] = await Promise.all([
        userDetailService.getUserBots(userId),
        userDetailService.getUserBotAccounts(userId)
      ]);
      
      setBots(botsData);
      setBotAccounts(botAccountsData);
    } catch (error) {
      console.error('Error fetching bots data:', error);
      toast.error('Failed to load bots data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const getBotStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            Inactive
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  const getBotTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'premium':
        return <TrendingUp className="h-4 w-4 text-yellow-500" />;
      case 'prop':
        return <BarChart3 className="h-4 w-4 text-purple-500" />;
      default:
        return <Bot className="h-4 w-4 text-blue-500" />;
    }
  };

  const getConnectedAccountsForBot = (botId: string) => {
    return botAccounts.filter(account => account.botId === botId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="p-4 border rounded-lg">
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-4 w-24 mb-3" />
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bots Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Bots</p>
                <p className="text-2xl font-bold">{bots.length}</p>
              </div>
              <Bot className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Bots</p>
                <p className="text-2xl font-bold">
                  {bots.filter(bot => bot.status.toLowerCase() === 'active').length}
                </p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Connected Accounts</p>
                <p className="text-2xl font-bold">{botAccounts.length}</p>
              </div>
              <Link className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User's Bots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            User's Bots
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bots.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No bots created yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bots.map((bot) => {
                const connectedAccounts = getConnectedAccountsForBot(bot.id);
                
                return (
                  <div key={bot.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getBotTypeIcon(bot.type)}
                        <h3 className="font-medium">{bot.name}</h3>
                      </div>
                      {getBotStatusBadge(bot.status)}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Type</span>
                        <Badge variant="outline">
                          {bot.type.charAt(0).toUpperCase() + bot.type.slice(1)}
                        </Badge>
                      </div>
                      
                      {/* Premium Bot Metrics */}
                      {bot.type === 'premium' && (
                        <>
                          {bot.winrate && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Win Rate</span>
                              <span className="text-sm font-medium text-green-600">{bot.winrate}</span>
                            </div>
                          )}
                          {bot.profitFactor && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Profit Factor</span>
                              <span className="text-sm font-medium">{bot.profitFactor}</span>
                            </div>
                          )}
                          {bot.subscribersTotal && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Subscribers</span>
                              <span className="text-sm font-medium">{bot.subscribersTotal.toLocaleString()}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Prop Bot Metrics */}
                      {bot.type === 'prop' && (
                        <>
                          {bot.passRate && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Pass Rate</span>
                              <span className="text-sm font-medium text-blue-600">{(bot.passRate * 100).toFixed(1)}%</span>
                            </div>
                          )}
                          {bot.propFirm && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Prop Firm</span>
                              <span className="text-sm font-medium">{bot.propFirm}</span>
                            </div>
                          )}
                          {bot.challengeDuration && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">Challenge Duration</span>
                              <span className="text-sm font-medium">{bot.challengeDuration}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Connected Accounts</span>
                        <span className="text-sm font-medium">{connectedAccounts.length}</span>
                      </div>
                      
                      {bot.tradingStyle && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Style</span>
                          <Badge variant="secondary">{bot.tradingStyle}</Badge>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Created</span>
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(bot.createdDate), { addSuffix: true })}
                        </span>
                      </div>
                    </div>

                    {/* Connected Accounts for this bot */}
                    {connectedAccounts.length > 0 && (
                      <div className="mt-4 pt-3 border-t">
                        <h4 className="text-sm font-medium mb-2">Connected Accounts</h4>
                        <div className="space-y-2">
                          {connectedAccounts.map((account) => (
                            <div key={account.id} className="text-xs bg-muted p-2 rounded">
                               <div className="flex items-center justify-between">
                                 <span>Volume: {account.volumeMultiplier}x</span>
                                 <ReversePositionBadge isReverse={account.isReversePosition} size="sm" />
                               </div>
                              <div className="space-y-1 text-muted-foreground">
                                <div>Status: {account.status}</div>
                                {account.balance && (
                                  <div>Balance: ${account.balance.toLocaleString()}</div>
                                )}
                                {account.broker && (
                                  <div>Broker: {account.broker}</div>
                                )}
                                {account.propFirm && (
                                  <div>Prop Firm: {account.propFirm}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bot Account Mappings */}
      {botAccounts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Bot-Account Mappings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {botAccounts.map((account) => {
                const bot = bots.find(b => b.id === account.botId);
                
                return (
                  <div key={account.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{account.botName || 'Unknown Bot'}</h4>
                        <Badge variant="outline" className="text-xs">
                          {account.botType || 'unknown'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Account ID: {account.tradingAccountId}
                      </p>
                      {account.balance && (
                        <p className="text-sm font-medium text-green-600">
                          Balance: ${account.balance.toLocaleString()}
                        </p>
                      )}
                      {(account.broker || account.propFirm) && (
                        <p className="text-xs text-muted-foreground">
                          {account.broker && `Broker: ${account.broker}`}
                          {account.propFirm && `Prop Firm: ${account.propFirm}`}
                        </p>
                      )}
                    </div>
                    
                    <div className="text-right space-y-1">
                       <div className="flex items-center gap-2">
                         <span className="text-sm font-medium">Volume: {account.volumeMultiplier}x</span>
                         <ReversePositionBadge isReverse={account.isReversePosition} size="sm" />
                       </div>
                      <Badge variant={account.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {account.status}
                      </Badge>
                      {account.isLive !== undefined && (
                        <Badge variant={account.isLive ? 'destructive' : 'secondary'} className="text-xs ml-2">
                          {account.isLive ? 'Live' : 'Demo'}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};